import { createContext, useCallback, useEffect, useReducer } from 'react';

import { getAllDocs } from 'src/lib/firebase';

//  ----------------------------------------------------

const initialState = {
  productPackages: [],
  isProductPackagesLoaded: false,
  isProductPackagesError: false,
  products: [],
  isProductsLoaded: false,
  isProductsError: false,
  categories: [],
  isCategoriesLoaded: false,
  isCategoriesError: false,
  motorSkills: [],
  isMotorSkillsLoaded: false,
  isMotorSkillsError: false,
  subscriptionEventOptions: [
    {
      id: 'all',
      name: 'All',
    },
    {
      id: 'activation',
      name: 'Activation',
    },
    {
      id: 'first_conversion',
      name: 'First conversion',
    },
    {
      id: 'entered_billing_retry',
      name: 'Entered billing retry',
    },
    {
      id: 'renewal',
      name: 'Renewal',
    },
    {
      id: 'cancellation',
      name: 'Cancellation',
    },
    {
      id: 'renewal_from_billing_retry',
      name: 'Renewal from billing retry',
    },
    {
      id: 'reactivation',
      name: 'Reactivation',
    },
    {
      id: 'renewal_extended',
      name: 'Renewal Extended',
    },
    {
      id: 'paused',
      name: 'Paused',
    },
  ],
  currencyOptions: [
    {
      id: 'all',
      name: 'All',
    },
    {
      id: 'TRY',
      name: 'TRY',
    },
    {
      id: 'USD',
      name: 'USD',
    },
    {
      id: 'EUR',
      name: 'EUR',
    },
  ],
  countryOptions: [
    {
      id: 'all',
      name: 'All',
    },
    {
      id: 'us',
      name: 'United States',
    },
    {
      id: 'tr',
      name: 'Turkey',
    },
    {
      id: 'de',
      name: 'Germany',
    },
    {
      id: 'gb',
      name: 'United Kingdom',
    },
    {
      id: 'it',
      name: 'Italy',
    },
  ],
};

const FiltersContext = createContext({
  ...initialState,
});

const filtersReducer = (state, action) => {
  switch (action.type) {
    case 'SET_PRODUCT_PACKAGES':
      return {
        ...state,
        productPackages: action.payload,
        isProductPackagesLoaded: true,
        isProductPackagesError: false,
      };
    case 'SET_IS_PRODUCT_PACKAGES_ERROR':
      return { ...state, productPackages: [], isProductPackagesLoaded: true, isProductPackagesError: true };
    case 'SET_PRODUCTS':
      return { ...state, products: action.payload, isProductsLoaded: true, isProductsError: false };
    case 'SET_IS_PRODUCTS_ERROR':
      return { ...state, products: [], isProductsLoaded: true, isProductsError: true };
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload, isCategoriesLoaded: true, isCategoriesError: false };
    case 'SET_IS_CATEGORIES_ERROR':
      return { ...state, categories: [], isCategoriesLoaded: true, isCategoriesError: true };
    case 'SET_MOTOR_SKILLS':
      return { ...state, motorSkills: action.payload, isMotorSkillsLoaded: true, isMotorSkillsError: false };
    case 'SET_IS_MOTOR_SKILLS_ERROR':
      return { ...state, motorSkills: [], isMotorSkillsLoaded: true, isMotorSkillsError: true };
    default:
      return state;
  }
};

function FiltersProvider({ children }) {
  const [state, dispatch] = useReducer(filtersReducer, initialState);

  const getProductPackages = useCallback(async () => {
    try {
      let productPackages = await getAllDocs('productPackages');
      productPackages = productPackages.map(productPackage => ({ id: productPackage.id, name: productPackage.name }));
      dispatch({ type: 'SET_PRODUCT_PACKAGES', payload: productPackages });
    } catch (error) {
      dispatch({ type: 'SET_IS_PRODUCT_PACKAGES_ERROR' });
    }
  }, []);

  useEffect(() => {
    getProductPackages();
  }, []);

  const getProducts = useCallback(async () => {
    try {
      let products = await getAllDocs('products');
      products = [{ id: 'all', providerId: 'All' }, ...products];
      products = products.map(product => ({ id: product.id, providerId: product.providerId }));
      dispatch({ type: 'SET_PRODUCTS', payload: products });
    } catch (error) {
      dispatch({ type: 'SET_IS_PRODUCTS_ERROR' });
    }
  }, []);

  useEffect(() => {
    getProducts();
  }, []);

  const getCategories = useCallback(async () => {
    try {
      let categories = await getAllDocs('categories');
      dispatch({ type: 'SET_CATEGORIES', payload: categories });
    } catch (error) {
      dispatch({ type: 'SET_IS_CATEGORIES_ERROR' });
    }
  }, []);

  useEffect(() => {
    getCategories();
  }, []);

  const getMotorSkills = useCallback(async () => {
    try {
      let motorSkills = await getAllDocs('motorSkills');
      dispatch({ type: 'SET_MOTOR_SKILLS', payload: motorSkills });
    } catch (error) {
      dispatch({ type: 'SET_IS_MOTOR_SKILLS_ERROR' });
    }
  }, []);

  useEffect(() => {
    getMotorSkills();
  }, []);

  return (
    <FiltersContext.Provider
      value={{
        ...state,
        getProductPackages,
        getProducts,
        getCategories,
        getMotorSkills,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
}

export { FiltersContext, FiltersProvider };
